import { useEffect, useRef, useState, type Key } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { deleteUser, fetchUsers, updateUserStatus } from "src/services/UserManagementAPIs";
import type { User } from "../Types/UserTypes";
import { GetErrorMessage } from "../Utils/ErrorMessage";
import { IsAdminUser } from "../Utils/UserPermissions";

type UserAccessChanges = Partial<Pick<User, "isActive" | "accessLevel">>;

export const useUsers = () => {
  const { t } = useTranslation();
  const apiKey = localStorage.getItem("api_key") || "";
  const tenantId = localStorage.getItem("tenant_id") || "";
  const hasFetchedUsers = useRef(false);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSocialListeningEnabled, setIsSocialListeningEnabled] = useState(true);
  const [deletingUserId, setDeletingUserId] = useState<number | null>(null);

  useEffect(() => {
    if (hasFetchedUsers.current) {
      return;
    }
    hasFetchedUsers.current = true;

    const loadUsers = async () => {
      try {
        const { users: fetchedUsers, socialListeningEnabled } = await fetchUsers(apiKey, tenantId);
        setUsers(fetchedUsers);
        setIsSocialListeningEnabled(socialListeningEnabled);
      } catch (error) {
        message.error(GetErrorMessage(error, t("userManagement.messages.failedFetchUsers")));
      } finally {
        setIsLoading(false);
      }
    };

    loadUsers();
  }, [apiKey, tenantId, t]);

  const updateUserAccess = async (user: User, changes: UserAccessChanges, failureMessage: string) => {
    const { isActive, accessLevel } = { ...user, ...changes };
    setUsers(prev => prev.map(item => (item.id === user.id ? { ...item, ...changes } : item)));
    try {
      await updateUserStatus(apiKey, user.id, isActive, accessLevel, tenantId);
    } catch (error) {
      message.error(GetErrorMessage(error, failureMessage));
    }
  };

  const toggleStatus = async (userId: number) => {
    const user = users.find(item => item.id === userId);
    if (user) {
      await updateUserAccess(
        user,
        { isActive: !user.isActive },
        t("userManagement.messages.failedUpdateUserStatus")
      );
    }
  };

  const changeAccessLevel = async (userId: number, accessLevel: string) => {
    const user = users.find(item => item.id === userId);
    if (user) {
      await updateUserAccess(user, { accessLevel }, t("userManagement.messages.failedUpdateAccessLevel"));
    }
  };

  const removeUser = async (userId: number) => {
    setDeletingUserId(userId);
    try {
      await deleteUser(apiKey, userId, tenantId);
      message.success(t("userManagement.messages.userDeletedSuccessfully"));
      setUsers(prev => prev.filter(user => user.id !== userId));
    } catch (error) {
      message.error(GetErrorMessage(error, t("userManagement.messages.failedDeleteUser")));
    } finally {
      setDeletingUserId(null);
    }
  };

  const removeUsers = async (userIds: Key[]): Promise<boolean> => {
    const deletableIds = users
      .filter(user => userIds.includes(user.id) && !IsAdminUser(user))
      .map(user => user.id);
    try {
      await Promise.all(deletableIds.map(id => deleteUser(apiKey, id, tenantId)));
      message.success(t("userManagement.messages.selectedUsersDeleted"));
      setUsers(prev => prev.filter(user => !deletableIds.includes(user.id)));
      return true;
    } catch (error) {
      message.error(GetErrorMessage(error, t("userManagement.messages.failedDeleteSelectedUsers")));
      return false;
    }
  };

  const addUser = (newUser: User) => setUsers(prev => [...prev, newUser]);

  const replaceUser = (updatedUser: User) =>
    setUsers(prev => prev.map(user => (user.id === updatedUser.id ? updatedUser : user)));

  return {
    users,
    isLoading,
    isSocialListeningEnabled,
    deletingUserId,
    apiKey,
    tenantId,
    toggleStatus,
    changeAccessLevel,
    removeUser,
    removeUsers,
    addUser,
    replaceUser,
  };
};
